import { SVGPath, ViewBox } from '@acies/core';
import { theme } from '@acies/ui';
import { Schema } from 'effect';
import { GeometryShapeTypes, type IGeometryShapeType } from '../../../domain/geometry-shape-types';

/** Whether a value is one of the shapes there are. */
const isGeometryShapeType = Schema.is(GeometryShapeTypes);

/**
 * A drawing of one of the shapes a window can be cut to, for the user to pick it by.
 *
 * An outline drawn with one path, the way `Icon` draws one, in a square of its own so every shape sits in the same box and a grid of them lines up. Which shape it is is {@link GeometrySVGShape.shapeType}.
 *
 * Drawn in a 48 by 48 box with the window from 6 to 42 each way, so the stroke has room on every side. The outline only: `SVG` leaves the fill off.
 */
export default class GeometrySVGShape extends SVGPath {
	/**
	 * The shape to draw, or null to draw none.
	 *
	 * One of the shapes the domain lists, and refused if it is not, the way `Icon` refuses a name it has no path for.
	 */
	public set shapeType(value: IGeometryShapeType | null) {
		assertIsValidShapeType(value);
		this.#shapeType = value;
		this.#shapeTypeChanged = true;
		this.invalidateProperties();
	}

	public get shapeType(): IGeometryShapeType | null {
		return this.#shapeType;
	}

	#shapeType: IGeometryShapeType | null;
	#shapeTypeChanged: boolean;

	public constructor() {
		super();
		this.#shapeType = null;
		this.#shapeTypeChanged = false;
		this.width = 48;
		this.height = 48;
		this.viewBox = new ViewBox(0, 0, 48, 48);
		this.strokeColor = theme.colors.icon.soft;
		this.strokeWeight = 2;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#shapeTypeChanged) {
			this.#commitShapeType();
		}
	}

	/** Draws the shape, which is no drawing at all for none. */
	#commitShapeType(): void {
		this.#shapeTypeChanged = false;
		this.commands = this.#commandsFor(this.#shapeType);
	}

	/**
	 * The path commands a shape is drawn with, or null for no shape.
	 *
	 * Drawn from the dumps of the old shape picker. A side named in a shape's type is the side cut away: a chamfer cuts the corner off, a slope cuts across the whole top or bottom so that side comes out shorter, and a triangle cuts all the way across so that side is gone. A shape at the bottom is the one at the top
	 * turned upside down. A chamfer cuts a third along both sides, a slope takes a third off the shorter side, and a rounded corner has a radius of a third.
	 *
	 * A switch with every shape a case of its own, each returning, and no default: TypeScript checks that the cases cover every shape, since a function that can fall out of the switch has no string to return. So a shape added to the domain and not drawn here does not compile, without a branch that could never run.
	 */
	#commandsFor(shapeType: IGeometryShapeType | null): string | null {
		switch (shapeType) {
			case null:
				return null;
			case 'rectangle':
				return 'M6 6 H42 V42 H6 Z';
			case 'circle':
				return 'M42 24 A18 18 0 1 1 6 24 A18 18 0 1 1 42 24 Z';
			case 'arch-top':
				return 'M6 42 V24 A18 18 0 0 1 42 24 V42 Z';
			case 'chamfer-top-left':
				return 'M6 18 L18 6 H42 V42 H6 Z';
			case 'chamfer-top-both':
				return 'M6 18 L18 6 H30 L42 18 V42 H6 Z';
			case 'chamfer-top-right':
				return 'M6 6 H30 L42 18 V42 H6 Z';
			case 'chamfer-bottom-left':
				return 'M6 6 H42 V42 H18 L6 30 Z';
			case 'chamfer-bottom-both':
				return 'M6 6 H42 V30 L30 42 H18 L6 30 Z';
			case 'chamfer-bottom-right':
				return 'M6 6 H42 V30 L30 42 H6 Z';
			case 'slope-top-left':
				return 'M6 18 L42 6 V42 H6 Z';
			case 'slope-top-right':
				return 'M6 6 L42 18 V42 H6 Z';
			case 'slope-bottom-left':
				return 'M6 6 H42 V42 L6 30 Z';
			case 'slope-bottom-right':
				return 'M6 6 H42 V30 L6 42 Z';
			case 'triangle-top-left':
				return 'M42 6 V42 H6 Z';
			case 'triangle-top-both':
				return 'M24 6 L42 42 H6 Z';
			case 'triangle-top-right':
				return 'M6 6 V42 H42 Z';
			case 'triangle-bottom-left':
				return 'M6 6 H42 V42 Z';
			case 'triangle-bottom-both':
				return 'M6 6 H42 L24 42 Z';
			case 'triangle-bottom-right':
				return 'M6 6 H42 L6 42 Z';
			case 'rounded-top-left':
				return 'M6 18 A12 12 0 0 1 18 6 H42 V42 H6 Z';
			case 'rounded-top-both':
				return 'M6 18 A12 12 0 0 1 18 6 H30 A12 12 0 0 1 42 18 V42 H6 Z';
			case 'rounded-top-right':
				return 'M6 6 H30 A12 12 0 0 1 42 18 V42 H6 Z';
		}
	}
}
customElements.define('cf-geometry-svg-shape', GeometrySVGShape);

function assertIsValidShapeType(value: unknown): asserts value is IGeometryShapeType | null {
	if (value === null || isGeometryShapeType(value)) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be one of the geometry shape types or null`);
}
